import { AmplifyAuthAdapter } from './auth/AmplifyAuthAdapter'
import { HttpClient } from './http/HttpClient'
import { HttpRenditionRepository } from './rendition/HttpRenditionRepository'
import type { SharedReceiptRepository } from '@/domain/rendition/SharedReceiptRepository'
import { HttpSharedReceiptRepository } from './rendition/HttpSharedReceiptRepository'
import { HttpUserRepository } from './user/HttpUserRepository'
import type { MyInvoiceRepository } from '@/domain/myInvoice/MyInvoiceRepository'
import { HttpMyInvoiceRepository } from './myInvoice/HttpMyInvoiceRepository'
import type { RenditionReviewRepository } from '@/domain/renditionReview/RenditionReviewRepository'
import { HttpRenditionReviewRepository } from './renditionReview/HttpRenditionReviewRepository'
import type { AdvanceRenditionRepository } from '@/domain/advanceRendition/AdvanceRenditionRepository'
import { HttpAdvanceRenditionRepository } from './advanceRendition/HttpAdvanceRenditionRepository'
import type { PaymentBankReturnRepository } from '@/domain/payment/BankReturn'
import { HttpPaymentBankReturnRepository } from './payment/HttpPaymentBankReturnRepository'
import type { ManualPaymentSplitRepository } from '@/domain/payment/ManualPaymentSplit'
import { HttpManualPaymentSplitRepository } from './payment/HttpManualPaymentSplitRepository'
import type { SocietySettingsRepository } from '@/domain/society/SocietySettings'
import { HttpSocietySettingsRepository } from './society/HttpSocietySettingsRepository'
import type { PaymentIndicatorRepository } from '@/domain/payment/PaymentIndicators'
import { HttpPaymentIndicatorRepository } from './payment/HttpPaymentIndicatorRepository'
import type { PaymentComplementRepository } from '@/domain/paymentComplement/PaymentComplement'
import { HttpPaymentComplementRepository } from './paymentComplement/HttpPaymentComplementRepository'
import type { RequestBulkReviewRepository } from '@/domain/requestReview/RequestBulkReviewRepository'
import { HttpRequestBulkReviewRepository } from './requestReview/HttpRequestBulkReviewRepository'
import type { MasterDataUnificationRepository } from '@/domain/masterData/MasterDataUnificationRepository'
import { HttpMasterDataUnificationRepository } from './masterData/HttpMasterDataUnificationRepository'
import type { GlobalSearchRepository } from '@/domain/search/GlobalSearch'
import { HttpGlobalSearchRepository } from './search/HttpGlobalSearchRepository'
import type { InboxRepository } from '@/domain/inbox/Inbox'
import type { BackgroundTaskRepository } from '@/domain/backgroundTask/BackgroundTask'
import { HttpBackgroundTaskRepository } from './backgroundTask/HttpBackgroundTaskRepository'
import { HttpInboxRepository } from './inbox/HttpInboxRepository'
import type { MyRequestsRepository } from '@/domain/home/MyRequests'
import { HttpMyRequestsRepository } from './home/HttpMyRequestsRepository'
import type { UsageEventRepository } from '@/domain/usage/UsageEvent'
import { HttpUsageEventRepository } from './usage/HttpUsageEventRepository'
import type { BudgetRepository } from '@/domain/home/Budget'
import { HttpBudgetRepository } from './home/HttpBudgetRepository'
import type { WeekScheduleRepository } from '@/domain/home/WeekSchedule'
import { HttpWeekScheduleRepository } from './home/HttpWeekScheduleRepository'
import type { ApprovalAnalyticsRepository } from '@/domain/analytics/ApprovalAnalytics'
import { HttpApprovalAnalyticsRepository } from './analytics/HttpApprovalAnalyticsRepository'
import type { IntegrationRepository } from '@/domain/integration/Integration'
import { HttpIntegrationRepository } from './integration/HttpIntegrationRepository'
import type { InvoiceMoveRepository } from '@/domain/contract/invoiceMove'
import { HttpInvoiceMoveRepository } from './contract/HttpInvoiceMoveRepository'
import type { BudgetDistributionRepository } from '@/domain/budgetDistribution/BudgetDistribution'
import { HttpBudgetDistributionRepository } from './budgetDistribution/HttpBudgetDistributionRepository'
import type { UnitRepository } from '@/domain/unit/Unit'
import { HttpUnitRepository } from './unit/HttpUnitRepository'
import type { EquivalenceRepository } from '@/domain/unit/Equivalence'
import { HttpEquivalenceRepository } from './unit/HttpEquivalenceRepository'
import type { ProductRepository } from '@/domain/product/Product'
import { HttpProductRepository } from './product/HttpProductRepository'
import type { ConnectorConsentRepository } from '@/domain/connector/ConnectorConsent'
import { HttpConnectorConsentRepository } from './connector/HttpConnectorConsentRepository'
import type { RequestGroupRepository } from '@/domain/requestGroup/RequestGroup'
import { HttpRequestGroupRepository } from './requestGroup/HttpRequestGroupRepository'
import type { GroupMemberDetailRepository } from '@/domain/requestGroup/groupMemberDetail'
import { HttpGroupMemberDetailRepository } from './requestGroup/HttpGroupMemberDetailRepository'

export const authAdapter = new AmplifyAuthAdapter()
export const httpClient = new HttpClient(authAdapter)
export const userRepository = new HttpUserRepository(httpClient)
export const renditionRepository = new HttpRenditionRepository(httpClient)
export const sharedReceiptRepository: SharedReceiptRepository = new HttpSharedReceiptRepository(httpClient)

// "Rendiciones" pool — real backend (API `06-invoice-pool`).
export const myInvoiceRepository: MyInvoiceRepository = new HttpMyInvoiceRepository(httpClient)

export const renditionReviewRepository: RenditionReviewRepository = new HttpRenditionReviewRepository(httpClient)

export const advanceRenditionRepository: AdvanceRenditionRepository = new HttpAdvanceRenditionRepository(httpClient)

export const paymentBankReturnRepository: PaymentBankReturnRepository =
  new HttpPaymentBankReturnRepository(httpClient)

export const manualPaymentSplitRepository: ManualPaymentSplitRepository =
  new HttpManualPaymentSplitRepository(httpClient)
export const societySettingsRepository: SocietySettingsRepository = new HttpSocietySettingsRepository(httpClient)
export const paymentIndicatorRepository: PaymentIndicatorRepository =
  new HttpPaymentIndicatorRepository(httpClient)
export const paymentComplementRepository: PaymentComplementRepository =
  new HttpPaymentComplementRepository(httpClient)

export const requestBulkReviewRepository: RequestBulkReviewRepository =
  new HttpRequestBulkReviewRepository(httpClient)
export const requestGroupRepository: RequestGroupRepository = new HttpRequestGroupRepository(httpClient)
export const groupMemberDetailRepository: GroupMemberDetailRepository = new HttpGroupMemberDetailRepository(httpClient)

export const masterDataUnificationRepository: MasterDataUnificationRepository =
  new HttpMasterDataUnificationRepository(httpClient)

export const globalSearchRepository: GlobalSearchRepository = new HttpGlobalSearchRepository(httpClient)
export const integrationRepository: IntegrationRepository = new HttpIntegrationRepository(httpClient)
export const invoiceMoveRepository: InvoiceMoveRepository = new HttpInvoiceMoveRepository(httpClient)

export const budgetDistributionRepository: BudgetDistributionRepository = new HttpBudgetDistributionRepository(httpClient)

export const inboxRepository: InboxRepository = new HttpInboxRepository(httpClient)
export const backgroundTaskRepository: BackgroundTaskRepository = new HttpBackgroundTaskRepository(httpClient)

export const myRequestsRepository: MyRequestsRepository = new HttpMyRequestsRepository(httpClient)

export const usageEventRepository: UsageEventRepository = new HttpUsageEventRepository(httpClient)
export const budgetRepository: BudgetRepository = new HttpBudgetRepository(httpClient)
export const weekScheduleRepository: WeekScheduleRepository = new HttpWeekScheduleRepository(httpClient)
export const approvalAnalyticsRepository: ApprovalAnalyticsRepository = new HttpApprovalAnalyticsRepository(httpClient)
export const unitRepository: UnitRepository = new HttpUnitRepository(httpClient)
export const equivalenceRepository: EquivalenceRepository = new HttpEquivalenceRepository(httpClient)
export const productRepository: ProductRepository = new HttpProductRepository(httpClient)
export const connectorConsentRepository: ConnectorConsentRepository = new HttpConnectorConsentRepository(httpClient)

// The Facturas rework repository is intentionally NOT exported here — see
// `src/infrastructure/invoice/index.ts` for why (it would drag the fixtures into every
// bundle that imports this barrel).
